/**
 * Admin → Portfolios → Assign trade rules (TRADE-CONS step 5a, migration 0134; Pedro O4: "rules
 * can be defined for each under portfolios as well (new tab - Assign trade rules)").
 *
 * One row per consolidation (S5-Q3): what the optimizer follows when the consolidation is traded
 * (S5-Q4) — the tolerance on each security's consolidated target (default 10 bps), the order of
 * the goals (S5-Q5: Keep accounts apart / Minimal trading / Tax-aware, or an own order; transfers
 * between banks always first; closest to the target just before the money goals, Pedro 2026-11-01), and per portfolio its minimum cash (in its reference currency,
 * default 0, O5) and the insurance wrapper it sits in (no cash transfer across wrappers). Until
 * saved, the defaults apply. Every value can still be changed for one run (step 5c).
 *
 * Edit → Save changes / Discard (§3g Pattern A, leave-page guard), as the Assign to… tabs; one
 * save per changed consolidation, a failure shown on its row. Changed by `portfolios.trade_rules`
 * (F2), greyed with the reason otherwise. Headings filter and sort (P2, U3).
 */
import { useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Group, NumberInput, Select, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowBackUp, IconArrowDown, IconArrowUp, IconPencil } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  TARGET_GOAL, TARGET_LABEL, tradeRulesApi, withTarget, type GoalKey, type RankingPreset, type TradeRules, type TradeRulesIn,
} from '../api/tradeRules';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { useGridHeaders, type GridCol } from '../components/GridHeaders';
import { useReportTabDirty } from '../components/TabDirty';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { usePermissions } from '../hooks/usePermissions';

const errMsg = (e: unknown) => (e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Something went wrong.');
const money = (s: string) => Number(s).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const asBody = (r: TradeRules): TradeRulesIn => ({
  tolerance_bps: r.tolerance_bps, preset: r.preset, ranking: [...r.ranking],
  members: r.members.map((m) => ({ link_id: m.link_id, min_cash: m.min_cash, wrapper: m.wrapper })),
});
const same = (a: TradeRulesIn, b: TradeRulesIn) => JSON.stringify(a) === JSON.stringify(b);

export function TradeRulesTab() {
  const { can } = usePermissions();
  const canSet = can('portfolios.trade_rules');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['trade-rules', 'list'], queryFn: () => tradeRulesApi.list() });
  const rows = useMemo(() => q.data?.items ?? [], [q.data]);
  const goals = q.data?.goals;
  const [editMode, setEditMode] = useState(false);
  const [staged, setStaged] = useState<Record<string, TradeRulesIn>>({});
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const bodyOf = (r: TradeRules) => staged[r.consolidation_id] ?? asBody(r);
  const changed = rows.filter((r) => staged[r.consolidation_id] && !same(staged[r.consolidation_id], asBody(r)));
  const dirty = editMode && changed.length > 0;
  const stage = (r: TradeRules, f: (b: TradeRulesIn) => TradeRulesIn) =>
    setStaged((s) => ({ ...s, [r.consolidation_id]: f(structuredClone(s[r.consolidation_id] ?? asBody(r))) }));

  const onSave = async (): Promise<boolean> => {
    setSaving(true);
    const fails: Record<string, string> = {};
    let n = 0;
    for (const r of changed) {
      try { await tradeRulesApi.save(r.consolidation_id, staged[r.consolidation_id]); n += 1; }
      catch (e) { fails[r.consolidation_id] = errMsg(e); }
    }
    setSaving(false);
    setFailed(fails);
    await qc.invalidateQueries({ queryKey: ['trade-rules'] });
    const nFail = Object.keys(fails).length;
    notifications.show({ color: nFail ? 'yellow' : 'green', title: nFail ? 'Partly saved' : 'Saved',
      message: [n ? `${n} consolidation${n === 1 ? '' : 's'}' rules saved` : null,
        nFail ? `${nFail} not saved (see the rows in red)` : null].filter(Boolean).join(' · ') || 'Nothing to save.' });
    if (nFail === 0) { setStaged({}); setEditMode(false); return true; }
    setStaged((s) => Object.fromEntries(Object.entries(s).filter(([k]) => k in fails)));
    return false;
  };
  const confirmDiscard = useDirtyFormGuard(dirty, onSave);
  useReportTabDirty(dirty);
  const discard = () => confirmDiscard(() => { setStaged({}); setFailed({}); setEditMode(false); });

  const orderText = (r: TradeRules) => withTarget(bodyOf(r).ranking)
    .map((g) => (g === TARGET_GOAL ? TARGET_LABEL : goals?.[g as GoalKey] ?? g)).join(' → ');
  const cols: GridCol<TradeRules>[] = useMemo(() => [
    { key: 'agreement', label: 'Agreement', ft: 'enum', text: (r) => r.agreement_label },
    { key: 'consolidation', label: 'Consolidation', ft: 'text', text: (r) => r.consolidation_name },
    { key: 'tolerance', label: 'Tolerance (bps)', ft: 'num', width: 120, text: (r) => r.tolerance_bps, num: (r) => Number(r.tolerance_bps) },
    { key: 'order', label: 'Goal order', ft: 'enum', width: 300, text: (r) => r.preset_label },
    { key: 'portfolios', label: 'Portfolios: minimum cash · insurance wrapper', ft: 'text', text: (r) => r.members.map((m) => m.label).join(', ') },
    { key: 'saved', label: 'Last saved', ft: 'text', width: 170, text: (r) => (r.saved ? r.updated_by_name ?? '' : 'Defaults (not saved)') },
  ], []);
  const g = useGridHeaders(rows, cols, 'consolidations');
  const editReason = !canSet ? 'Changing trade rules needs the "portfolios.trade_rules" permission.'
    : rows.length === 0 ? 'No consolidation yet: make one in Assign to… → Attach to Consolidations.' : undefined;
  const presetOptions = (q.data ? (Object.keys(q.data.preset_labels) as RankingPreset[]) : [])
    .map((p) => ({ value: p, label: q.data!.preset_labels[p] }));

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end">
        <Text size="xs" c="dimmed" maw={900}>
          What the optimizer follows when a consolidation is traded: each security&apos;s consolidated weight within the
          tolerance of its target; the goals met in this order (fewest transfers between different banks always first;
          closest to the target just before traded value and realised gains); no portfolio&apos;s cash below its minimum; no cash transfer between portfolios
          in different insurance wrappers (or between one in a wrapper and one outside). Until saved, the defaults apply.
          Every value can still be changed for one run.
        </Text>
        {!editMode ? (
          <DisabledTooltipButton leftSection={<IconPencil size={16} />} onClick={() => setEditMode(true)} disabledReason={editReason}>Edit</DisabledTooltipButton>
        ) : (
          <Group gap="xs">
            <Button variant="default" leftSection={<IconArrowBackUp size={16} />} onClick={discard}>Discard</Button>
            <DisabledTooltipButton color="azul" loading={saving} disabledReason={dirty ? undefined : 'Nothing has changed.'}
              onClick={() => void onSave()}>Save changes{changed.length ? ` (${changed.length})` : ''}</DisabledTooltipButton>
          </Group>
        )}
      </Group>
      {q.error && <Alert color="red">{errMsg(q.error)}</Alert>}
      {g.summary}
      <Table striped withTableBorder fz="sm" verticalSpacing={6}>
        <Table.Thead><Table.Tr>{cols.map((c) => g.head(c))}</Table.Tr></Table.Thead>
        <Table.Tbody>
          {g.shown.length === 0 && !q.isLoading && (
            <Table.Tr><Table.Td colSpan={cols.length}><Text size="sm" c="dimmed">No consolidation yet: make one in Assign to… → Attach to Consolidations.</Text></Table.Td></Table.Tr>
          )}
          {g.shown.map((r) => {
            const b = bodyOf(r);
            const isChanged = !!staged[r.consolidation_id] && !same(staged[r.consolidation_id], asBody(r));
            const move = (i: number, d: -1 | 1) => stage(r, (x) => {
              const o = [...x.ranking]; [o[i], o[i + d]] = [o[i + d], o[i]];
              return { ...x, preset: 'custom', ranking: o };
            });
            return (
              <Table.Tr key={r.consolidation_id} style={{ verticalAlign: 'top' }}
                bg={failed[r.consolidation_id] ? 'var(--mantine-color-red-0)' : isChanged ? 'var(--mantine-color-orange-0)' : undefined}>
                <Table.Td>{r.agreement_label}</Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>{r.consolidation_name}</Text>
                  {failed[r.consolidation_id] && <Text size="xs" c="red">{failed[r.consolidation_id]}</Text>}
                </Table.Td>
                <Table.Td>
                  {editMode ? (
                    <NumberInput size="xs" w={100} min={0.01} max={1000} decimalScale={2} value={b.tolerance_bps}
                      onChange={(v) => stage(r, (x) => ({ ...x, tolerance_bps: v === '' ? '' : String(v) }))}
                      description={`= ${(Number(b.tolerance_bps || 0) / 100).toFixed(2)} %`} />
                  ) : <Text size="sm">{r.tolerance_bps} <Text span size="xs" c="dimmed">({(Number(r.tolerance_bps) / 100).toFixed(2)} %)</Text></Text>}
                </Table.Td>
                <Table.Td>
                  {editMode ? (
                    <Stack gap={4}>
                      <Select size="xs" data={presetOptions} value={b.preset} allowDeselect={false}
                        onChange={(p) => p && stage(r, (x) => ({ ...x, preset: p as RankingPreset,
                          ranking: p === 'custom' ? x.ranking : [...q.data!.presets[p as Exclude<RankingPreset, 'custom'>]] }))} />
                      {/* Pedro 2026-11-01 ("Yes, before them"): "Closest to the target" sits just before the
                          first money goal (traded value or gains), wherever the order puts them. */}
                      {withTarget(b.ranking).map((gk, shown) => {
                        if (gk === TARGET_GOAL) {
                          return (
                            <Tooltip key={gk} withArrow multiline w={300} label="Always just before traded value and realised gains: otherwise every trade would stop at the tolerance's edge to trade or realise a little less.">
                              <Text size="xs" c="dimmed">{shown + 1}. {TARGET_LABEL} (just before traded value / gains)</Text>
                            </Tooltip>
                          );
                        }
                        const i = b.ranking.indexOf(gk as GoalKey);
                        return (
                          <Group key={gk} gap={4} wrap="nowrap">
                            <Text size="xs" w={14} c="dimmed">{shown + 1}.</Text>
                            <Text size="xs" style={{ flex: 1 }}>{goals?.[gk as GoalKey] ?? gk}</Text>
                            <Tooltip withArrow label={i === 0 ? 'Always first' : 'Move up'}>
                              <ActionIcon size="xs" variant="subtle" disabled={i <= 1} onClick={() => move(i, -1)} aria-label="Move up"><IconArrowUp size={12} /></ActionIcon>
                            </Tooltip>
                            <Tooltip withArrow label={i === 0 ? 'Always first' : 'Move down'}>
                              <ActionIcon size="xs" variant="subtle" disabled={i === 0 || i === b.ranking.length - 1} onClick={() => move(i, 1)} aria-label="Move down"><IconArrowDown size={12} /></ActionIcon>
                            </Tooltip>
                          </Group>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Tooltip withArrow multiline w={360} label={orderText(r)}>
                      <Badge variant="light" color="gray" style={{ textTransform: 'none' }}>{r.preset_label}</Badge>
                    </Tooltip>
                  )}
                </Table.Td>
                <Table.Td>
                  <Stack gap={4}>
                    {r.members.map((m, i) => (
                      <Group key={m.link_id} gap="xs" wrap="nowrap">
                        <Text size="sm" w={150} style={{ whiteSpace: 'nowrap' }}>{m.label}</Text>
                        {editMode ? (<>
                          <NumberInput size="xs" w={140} min={0} decimalScale={2} thousandSeparator=" "
                            rightSection={<Text size="xs" c="dimmed">{m.ref_ccy ?? ''}</Text>} rightSectionWidth={40}
                            value={b.members[i]?.min_cash ?? '0'} aria-label={`Minimum cash of ${m.label}`}
                            onChange={(v) => stage(r, (x) => ({ ...x, members: x.members.map((y, j) => (j === i ? { ...y, min_cash: v === '' ? '0' : String(v) } : y)) }))} />
                          <TextInput size="xs" w={160} placeholder="No wrapper" maxLength={60} value={b.members[i]?.wrapper ?? ''}
                            aria-label={`Insurance wrapper of ${m.label}`}
                            onChange={(e) => { const v = e.currentTarget.value; stage(r, (x) => ({ ...x, members: x.members.map((y, j) => (j === i ? { ...y, wrapper: v || null } : y)) })); }} />
                        </>) : (<>
                          <Text size="sm" w={140} ta="right">{money(m.min_cash)} <Text span size="xs" c="dimmed">{m.ref_ccy ?? ''}</Text></Text>
                          <Text size="sm" c={m.wrapper ? undefined : 'dimmed'}>{m.wrapper ?? 'No wrapper'}</Text>
                        </>)}
                      </Group>
                    ))}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  {r.saved ? <Text size="xs">{r.updated_by_name ?? '-'} · {r.updated_at ? new Date(r.updated_at).toLocaleString() : ''}</Text>
                    : <Text size="xs" c="dimmed">Defaults (not saved)</Text>}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
